import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useLang from '../hooks/useLang';

function getStatus(T) {
  return {
    paid:    { bg: 'bg-green-500',  text: 'text-white',      label: T.report.paid },
    partial: { bg: 'bg-yellow-400', text: 'text-yellow-900', label: T.report.partial },
    gap:     { bg: 'bg-red-500',    text: 'text-white',      label: T.report.gap },
    future:  { bg: 'bg-gray-100',   text: 'text-gray-400',   label: T.memberDetail.statusFuture },
  };
}

const fmt = (v) => new Intl.NumberFormat('sw-TZ').format(Math.round(v || 0));

function monthLabel(ym) {
  const [y, m] = ym.split('-');
  const names = ['Jan','Feb','Mac','Apr','Mei','Jun','Jul','Ago','Sep','Okt','Nov','Des'];
  return `${names[+m - 1]} ${y.slice(2)}`;
}

export default function MonthlyReport() {
  const { T } = useLang();
  const STATUS = getStatus(T);
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFullscreenReport, setShowFullscreenReport] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    api.get(`/groups/${id}/monthly-report`)
      .then(r => {
        setData(r.data);
        // Default to the latest year that has data
        if (r.data?.months?.length) {
          const lastYear = +r.data.months[r.data.months.length - 1].slice(0, 4);
          setYear(lastYear);
        }
      })
      .catch(e => setError(e.response?.data?.error || T.errLoad))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;
  if (error) return (
    <div className="max-w-xl">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-amber-800">
        <p className="font-semibold mb-1">{T.report.cannotShow}</p>
        <p className="text-sm">{error}</p>
        <Link to={`/groups/${id}/settings`}
          className="mt-3 inline-block bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">
          {T.report.goSettings}
        </Link>
      </div>
    </div>
  );
  if (!data) return null;

  const { group, months, monthly_amount, members } = data;

  // Year navigation
  const years = [...new Set(months.map(m => +m.slice(0, 4)))].sort();
  const yearIdx = years.indexOf(year);
  const filteredMonths = months.filter(m => m.startsWith(year));

  // Filter each member's months to the selected year
  const filteredMembers = members.map(m => ({
    ...m,
    months: m.months.filter(mx => mx.month.startsWith(year)),
  }));

  // Summary stats (for selected year only)
  const paidCells    = filteredMembers.reduce((s, m) => s + m.months.filter(x => x.status === 'paid').length, 0);
  const gapCells     = filteredMembers.reduce((s, m) => s + m.months.filter(x => x.status === 'gap').length, 0);
  const partialCells = filteredMembers.reduce((s, m) => s + m.months.filter(x => x.status === 'partial').length, 0);

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(135deg,rgba(15,118,110,0.96),rgba(15,23,42,0.94))] px-5 py-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:px-6">
        <Link to={`/groups/${id}`} className="inline-flex rounded-full bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-50">
          ← {group.name}
        </Link>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-bold sm:text-3xl">{T.report.title}</h1>
            <p className="mt-2 text-xs text-emerald-50/80 sm:text-sm">
              {T.report.subtitle}
            </p>
          </div>
          <div className="hidden sm:flex h-16 w-16 shrink-0 items-center justify-center rounded-[24px] bg-white/12 text-3xl shadow-inner shadow-white/10">
            📅
          </div>
        </div>

        {/* Year navigator */}
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={() => yearIdx > 0 && setYear(years[yearIdx - 1])}
            disabled={yearIdx <= 0}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white disabled:opacity-30 hover:bg-white/25 transition-colors"
          >
            ‹
          </button>
          <span className="text-2xl font-bold tracking-wide text-white">{year}</span>
          <button
            onClick={() => yearIdx < years.length - 1 && setYear(years[yearIdx + 1])}
            disabled={yearIdx >= years.length - 1}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white disabled:opacity-30 hover:bg-white/25 transition-colors"
          >
            ›
          </button>
          <span className="ml-1 text-xs text-emerald-100/60">
            {filteredMonths.length} {filteredMonths.length === 1 ? 'mwezi' : 'miezi'}
          </span>
        </div>
      </div>

      {/* Legend + summary */}
      <div className="rounded-[26px] border border-white/75 bg-white/88 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
        <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-3">
          <div className="flex flex-wrap gap-3 items-center">
            {Object.entries(STATUS).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5 text-xs">
                <div className={`w-3 h-3 rounded-sm ${v.bg}`} />
                <span className="text-gray-600">{v.label}</span>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500">
            {T.report.feeLabel}: <strong>{fmt(monthly_amount)} TZS/{T.common.date}</strong>
          </div>
        </div>
      </div>

      <div className={`grid gap-3 ${partialCells > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <div className="rounded-[24px] border border-green-200 bg-[linear-gradient(135deg,rgba(240,253,244,0.96),rgba(220,252,231,0.92))] p-3 sm:p-4">
          <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.12em] sm:tracking-[0.18em] text-green-600">{T.report.paid}</div>
          <div className="mt-1 sm:mt-2 text-xl sm:text-2xl font-bold text-green-700">{paidCells}</div>
          <div className="text-[10px] sm:text-xs text-green-600">{T.report.paidCells}</div>
        </div>
        <div className="rounded-[24px] border border-red-200 bg-[linear-gradient(135deg,rgba(254,242,242,0.96),rgba(254,226,226,0.92))] p-3 sm:p-4">
          <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.12em] sm:tracking-[0.18em] text-red-600">{T.report.gap}</div>
          <div className="mt-1 sm:mt-2 text-xl sm:text-2xl font-bold text-red-700">{gapCells}</div>
          <div className="text-[10px] sm:text-xs text-red-600">{T.report.gapCells}</div>
        </div>
        {partialCells > 0 && (
          <div className="rounded-[24px] border border-yellow-200 bg-[linear-gradient(135deg,rgba(254,252,232,0.96),rgba(254,249,195,0.92))] p-3 sm:p-4">
            <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.12em] sm:tracking-[0.18em] text-yellow-700">{T.report.partial}</div>
            <div className="mt-1 sm:mt-2 text-xl sm:text-2xl font-bold text-yellow-800">{partialCells}</div>
            <div className="text-[10px] sm:text-xs text-yellow-700">{T.report.partialCells}</div>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="space-y-3">
        <div className="space-y-3 md:hidden">
          <button
            type="button"
            onClick={() => setShowFullscreenReport(true)}
            className="block w-full overflow-hidden rounded-[24px] border border-white/75 bg-white/88 p-4 text-left shadow-[0_14px_35px_rgba(15,23,42,0.08)]"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">{T.report.reportCalendar}</p>
                <p className="mt-1 text-xs text-slate-500">{T.report.reportCalendarHint}</p>
              </div>
              <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                {T.report.openReport}
              </span>
            </div>

            <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex min-w-max gap-2">
                {filteredMonths.slice(0, Math.min(filteredMonths.length, 12)).map((m) => (
                  <div key={m} className="min-w-[68px] rounded-lg bg-white px-2 py-2 text-center shadow-sm">
                    <div className="text-[10px] font-semibold text-slate-600">{monthLabel(m).split(' ')[0]}</div>
                    <div className="mt-1 text-[10px] text-slate-400">{m.slice(2, 4)}</div>
                  </div>
                ))}
              </div>
            </div>
          </button>

          {filteredMembers.map(member => {
            const pastMonths = member.months.filter(mx => mx.status !== 'future');
            const gaps = pastMonths.filter(mx => mx.status === 'gap').length;
            const lastMx = pastMonths[pastMonths.length - 1];
            const memberBalance = lastMx?.true_balance ?? lastMx?.balance ?? 0;

            return (
              <div key={member.id} className="rounded-[24px] border border-white/75 bg-white/88 p-3 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <Link to={`/members/${member.id}`} className="block truncate font-semibold text-green-700 hover:text-green-900">
                      {member.name}
                    </Link>
                    <div className="mt-0.5 flex items-center gap-2">
                      <p className="text-xs text-slate-400">{member.phone}</p>
                      {gaps > 0 && <span className="text-xs font-semibold text-red-500">{gaps} {T.report.gapCount}</span>}
                    </div>
                  </div>
                  <div className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${memberBalance < 0 ? 'bg-red-50 text-red-600' : memberBalance > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    {memberBalance < 0 ? '−' : memberBalance > 0 ? '+' : ''}{fmt(Math.abs(memberBalance))}
                  </div>
                </div>

                <div className="mt-2.5 flex gap-1.5 overflow-x-auto pb-1">
                  {member.months.map((mx) => {
                    const s = STATUS[mx.status] || STATUS.future;
                    return (
                      <div key={mx.month} className="min-w-[52px] shrink-0 text-center" title={buildTooltip(mx)}>
                        <div className={`mx-auto flex h-9 w-[46px] items-center justify-center rounded-lg text-xs font-medium ${s.bg} ${s.text}`}>
                          {mx.status === 'paid' ? '✓' : mx.status === 'gap' ? '✗' : mx.status === 'partial' ? '½' : '·'}
                        </div>
                        <div className="mt-0.5 text-[10px] font-medium text-slate-400">{monthLabel(mx.month).split(' ')[0]}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
          <table className="text-xs border-collapse min-w-max">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-4 py-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 min-w-36 z-10">
                  {T.report.member}
                </th>
                {filteredMonths.map(m => (
                  <th key={m} className={`px-2 py-3 font-medium text-center min-w-14 ${
                    m === new Date().toISOString().slice(0,7) ? 'bg-green-50 text-green-700' : 'text-gray-500'
                  }`}>
                    {monthLabel(m)}
                  </th>
                ))}
                <th className="px-3 py-3 text-right font-semibold text-gray-700 min-w-24">Salio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMembers.map(member => {
                const pastMonths = member.months.filter(mx => mx.status !== 'future');
                const gaps = pastMonths.filter(mx => mx.status === 'gap').length;
                const lastMx = pastMonths[pastMonths.length - 1];
                const memberBalance = lastMx?.true_balance ?? lastMx?.balance ?? 0;

                return (
                  <tr key={member.id} className="hover:bg-gray-50 group">
                    <td className="px-4 py-2 sticky left-0 bg-white group-hover:bg-gray-50 z-10">
                      <Link to={`/members/${member.id}`}
                        className="font-medium text-green-700 hover:text-green-900 block">
                        {member.name}
                      </Link>
                      <span className="text-gray-400 text-xs">{member.phone}</span>
                      {gaps > 0 && (
                        <span className="ml-1 text-red-500 text-xs font-semibold">({gaps} pengo)</span>
                      )}
                    </td>

                    {member.months.map(mx => {
                      const s = STATUS[mx.status] || STATUS.future;
                      return (
                        <td key={mx.month} className="px-1 py-2 text-center">
                          <div
                            className={`mx-auto w-11 h-7 rounded flex items-center justify-center cursor-default text-xs font-medium ${s.bg} ${s.text}`}
                            title={buildTooltip(mx)}
                          >
                            {mx.status === 'paid'
                              ? '✓'
                              : mx.status === 'gap'
                              ? '✗'
                              : mx.status === 'partial'
                              ? '½'
                              : '·'}
                          </div>
                          {mx.paid_this_month > 0 && mx.status !== 'future' && (
                            <div className="text-gray-400 mt-0.5" style={{ fontSize: '9px' }}>
                              {fmt(mx.paid_this_month)}
                            </div>
                          )}
                        </td>
                      );
                    })}

                    <td className={`px-3 py-2 text-right font-semibold ${memberBalance < 0 ? 'text-red-600' : memberBalance > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {memberBalance < 0 ? '−' : memberBalance > 0 ? '+' : ''}{fmt(Math.abs(memberBalance))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t bg-gray-50 font-semibold">
                <td className="px-4 py-3 sticky left-0 bg-gray-50 text-gray-700">{T.report.totalMonth}</td>
                {filteredMonths.map(m => {
                  const colTotal = filteredMembers.reduce((s, mem) => {
                    const mx = mem.months.find(x => x.month === m);
                    return s + (mx?.paid_this_month || 0);
                  }, 0);
                  return (
                    <td key={m} className="px-2 py-3 text-center text-green-700 text-xs">
                      {colTotal > 0 ? fmt(colTotal) : '—'}
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-right text-green-700">
                  {fmt(filteredMembers.reduce((s, m) => s + m.months.reduce((ss, mx) => ss + mx.paid_this_month, 0), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Members with gaps summary */}
      {gapCells > 0 && (
        <div className="rounded-[24px] border border-red-200 bg-red-50 p-4">
          <h3 className="text-sm font-semibold text-red-800 mb-3">⚠️ {T.report.membersWithGaps}</h3>
          <div className="space-y-3">
            {filteredMembers
              .filter(m => m.months.some(mx => mx.status === 'gap'))
              .map(m => {
                const gapMonths = m.months
                  .filter(mx => mx.status === 'gap')
                  .map(mx => monthLabel(mx.month));
                const lastMonth = m.months.filter(mx => mx.status !== 'future').slice(-1)[0];
                const balance = lastMonth?.true_balance ?? lastMonth?.balance ?? 0;
                return (
                  <div key={m.id} className="rounded-[20px] border border-red-100 bg-white px-4 py-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Link to={`/members/${m.id}`} className="font-medium text-gray-800 hover:text-green-700">
                          {m.name}
                        </Link>
                        <p className="mt-1 text-xs text-gray-500">{m.phone}</p>
                      </div>
                      <div className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-600">
                        {T.report.debtLabel}: {fmt(Math.abs(balance))} TZS
                      </div>
                    </div>

                    <div className="mt-3 rounded-2xl bg-red-50 px-3 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-400">{T.report.gapLabel}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {gapMonths.map((gapMonth) => (
                          <span
                            key={`${m.id}-${gapMonth}`}
                            className="rounded-2xl border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-700"
                          >
                            {gapMonth}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {showFullscreenReport && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm md:hidden">
          <div className="flex h-full flex-col bg-white">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">Kalenda ya Ripoti</p>
                <p className="text-xs text-slate-500">{group.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowFullscreenReport(false)}
                className="rounded-2xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700"
              >
                {T.report.closeFS}
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                <table className="text-xs border-collapse min-w-max">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-4 py-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 min-w-36 z-10">
                        Mwanachama
                      </th>
                      {filteredMonths.map(m => (
                        <th key={m} className={`px-2 py-3 font-medium text-center min-w-14 ${
                          m === new Date().toISOString().slice(0,7) ? 'bg-green-50 text-green-700' : 'text-gray-500'
                        }`}>
                          {monthLabel(m)}
                        </th>
                      ))}
                      <th className="px-3 py-3 text-right font-semibold text-gray-700 min-w-24">Salio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredMembers.map(member => {
                      const pastMonths = member.months.filter(mx => mx.status !== 'future');
                      const gaps = pastMonths.filter(mx => mx.status === 'gap').length;
                      const lastMx = pastMonths[pastMonths.length - 1];
                      const memberBalance = lastMx?.true_balance ?? lastMx?.balance ?? 0;

                      return (
                        <tr key={member.id} className="hover:bg-gray-50 group">
                          <td className="px-4 py-2 sticky left-0 bg-white group-hover:bg-gray-50 z-10">
                            <Link to={`/members/${member.id}`}
                              className="font-medium text-green-700 hover:text-green-900 block"
                              onClick={() => setShowFullscreenReport(false)}
                            >
                              {member.name}
                            </Link>
                            <span className="text-gray-400 text-xs">{member.phone}</span>
                            {gaps > 0 && (
                              <span className="ml-1 text-red-500 text-xs font-semibold">({gaps} pengo)</span>
                            )}
                          </td>

                          {member.months.map(mx => {
                            const s = STATUS[mx.status] || STATUS.future;
                            return (
                              <td key={mx.month} className="px-1 py-2 text-center">
                                <div
                                  className={`mx-auto w-11 h-7 rounded flex items-center justify-center cursor-default text-xs font-medium ${s.bg} ${s.text}`}
                                  title={buildTooltip(mx)}
                                >
                                  {mx.status === 'paid'
                                    ? '✓'
                                    : mx.status === 'gap'
                                    ? '✗'
                                    : mx.status === 'partial'
                                    ? '½'
                                    : '·'}
                                </div>
                                {mx.paid_this_month > 0 && mx.status !== 'future' && (
                                  <div className="text-gray-400 mt-0.5" style={{ fontSize: '9px' }}>
                                    {fmt(mx.paid_this_month)}
                                  </div>
                                )}
                              </td>
                            );
                          })}

                          <td className={`px-3 py-2 text-right font-semibold ${memberBalance < 0 ? 'text-red-600' : memberBalance > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {memberBalance < 0 ? '−' : memberBalance > 0 ? '+' : ''}{fmt(Math.abs(memberBalance))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t bg-gray-50 font-semibold">
                      <td className="px-4 py-3 sticky left-0 bg-gray-50 text-gray-700">{T.report.totalMonth}</td>
                      {filteredMonths.map(m => {
                        const colTotal = filteredMembers.reduce((s, mem) => {
                          const mx = mem.months.find(x => x.month === m);
                          return s + (mx?.paid_this_month || 0);
                        }, 0);
                        return (
                          <td key={m} className="px-2 py-3 text-center text-green-700 text-xs">
                            {colTotal > 0 ? fmt(colTotal) : '—'}
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-right text-green-700">
                        {fmt(filteredMembers.reduce((s, m) => s + m.months.reduce((ss, mx) => ss + mx.paid_this_month, 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function buildTooltip(mx) {
  const label = mx.status;
  const n = new Intl.NumberFormat('sw-TZ');
  const isGap = mx.status === 'gap' || mx.status === 'partial';
  const displayBalance = isGap
    ? (mx.true_balance ?? mx.balance)
    : mx.balance;
  const balanceLabel = isGap ? 'Jumla deni' : 'Ziada';
  const lines = [
    `Mwezi: ${mx.month}`,
    `Hali: ${label}`,
    `Alipolipa mwezi huu: ${n.format(mx.paid_this_month)} TZS`,
    `Jumla aliyolipa: ${n.format(mx.cumulative_paid)} TZS`,
    `Anastahili kulipa: ${n.format(mx.expected_so_far)} TZS`,
    `${balanceLabel}: ${n.format(Math.abs(displayBalance))} TZS`,
  ];
  return lines.join('\n');
}
